/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * listings-webapp. Mirrors the Valkyrie-app pattern documented in
 * `docs/orion-feature-flags.md` (creator-navigation PR #381):
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsRelatedEventsDenseAdsEnabled`).
 *
 * To add a new flag: append one key + one default to `ListingsWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';

export type ListingsWebappSettings = {
  enable_increase_impressions_on_related_events: boolean;
  show_follow_venue_cta: boolean;
  show_follow_artist_cta: boolean;
  members_experiments_excluded_organizer_ids: string[];
  related_higher_price: boolean;
  enable_related_events_ad_prefetch: boolean;
};

const DEFAULT_SETTINGS = createAppDefaults<ListingsWebappSettings>({
  enable_increase_impressions_on_related_events: false,
  show_follow_venue_cta: false,
  show_follow_artist_cta: false,
  members_experiments_excluded_organizer_ids: [],
  related_higher_price: false,
  enable_related_events_ad_prefetch: false,
});

export const useBspConfig = () =>
  useMemo(() => {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';
    const environment = domain.includes('evbqa')
      ? 'staging'
      : domain.includes('localhost')
        ? 'development'
        : 'production';

    return {
      appName: 'listings-webapp',
      environment,
      defaultOrionSettings: DEFAULT_SETTINGS,
      defaultSegmentations: {},
    } as const;
  }, []);

/**
 * Gate for the denser related-events ad layout
 * (`enable_increase_impressions_on_related_events` Orion flag).
 *
 *   false (default / control) — request `slots: 2` and interleave ads via
 *     `interleaveArrays(promoted, organic)` (positions [1, 3]).
 *   true  (treatment)         — request `slots: 3` and interleave at
 *     positions [1, 2, 4] (ads → ads → organic → ads → organic).
 *
 * Same key + same default in eb-ui's `packages/attendee/listings/src/components/_shared`
 * and `packages/attendee/event-recommendations/src` so all three surfaces
 * ramp in lockstep.
 *
 * Excluded organizers: when `parentEventOrganizerId` is one of the ids in
 * the Orion setting `members_experiments_excluded_organizer_ids`, this
 * hook returns `false` regardless of the assigned segment — mirroring
 * the eb-ui checkout / event-recommendations gate so all three surfaces
 * opt the same events out in lockstep.
 */
export const useIsRelatedEventsDenseAdsEnabled = (parentEventOrganizerId?: string): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  const isOrganizerExcluded =
    !!parentEventOrganizerId && settings.members_experiments_excluded_organizer_ids.includes(parentEventOrganizerId);
  if (isOrganizerExcluded) {
    return false;
  }
  return settings.enable_increase_impressions_on_related_events;
};

/** Gate for the Follow Venue CTA (`show_follow_venue_cta` Orion flag). */
export const useShowFollowVenueCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_venue_cta;
};

/** Gate for the Follow Artist CTA (`show_follow_artist_cta` Orion flag). */
export const useShowFollowArtistCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_artist_cta;
};

/**
 * Gate for the higher-price related events experiment (`related_higher_price`
 * Orion flag). When on, the related-events request asks the search service to
 * zero out related events cheaper than the current event, so the carousel skews
 * towards equal-or-higher-priced events.
 *
 * Returns `isLoading` (unlike the other gates) so the related-events fetch can
 * wait for Orion to settle — otherwise a treatment visitor whose Orion resolves
 * after the fetch fires would be read as control and silently get the
 * unfiltered carousel, contaminating the experiment.
 */
export const useIsRelatedHigherPriceEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  return { isEnabled: settings.related_higher_price ?? false, isLoading };
};

/**
 * Gate for the related-events ad prefetch experiment (`enable_related_events_ad_prefetch` Orion flag).
 *
 * When on, the lazy-load observer for the related-events section gets a wider `rootMargin`, so the
 * promoted-events fetch fires earlier and the slot-1 ad card has time to mount before the visitor scrolls
 * into it. The two margins are `CONTROL_AD_PREFETCH_MARGIN_PX` / `TREATMENT_AD_PREFETCH_MARGIN_PX` in
 * `useRelatedAndPromotedEventsLazy`; `RelatedEvents` maps this boolean onto them.
 *
 * Why this is the lever: web `related_events` loses slot-1 impressions on ~170K sessions / 3 days — those sessions fire impressions on slots 2 and 3 but never slot 1, because the card mounts after the visitor has already scrolled past its position.
 * No other surface shows more than ~5% of this pattern.
 * The billable per-card beacon needs 25% of the card in viewport with no `rootMargin` of its own (`EventCardTracking`), so the card has to exist before the visitor arrives.
 *
 * Boolean rather than a numeric margin deliberately. Every multi-arm Orion experiment we run models its arms
 * as named string segments (see eb-ui checkout), and every on/off gate is a boolean — a raw number has no
 * precedent on the platform. The initiative also specifies a single treatment, not a set of arms, and the
 * treatment magnitude must not move mid-experiment anyway, so there is nothing to tune remotely.
 *
 * Deliberately does NOT return `isLoading`, unlike the higher-price and linear-score gates.
 * Those hold the fetch until Orion settles; holding the fetch here would defeat the treatment, whose entire content is "fetch earlier".
 * Instead the observer is rebuilt when the value settles (the options object is memoized on the margin, so a changed margin re-creates the observer).
 * The one-shot observer has not fired yet in the common case — the section sits far below the fold — so the treatment applies.
 * A visitor whose Orion settles after the section is already in range keeps control timing; that is the same tolerable-loss tradeoff documented on the dense-ads gate.
 */
export const useIsRelatedEventsAdPrefetchEnabled = (): boolean => {
  let settings: Partial<ListingsWebappSettings> = {};
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
  } catch {}
  return settings.enable_related_events_ad_prefetch ?? false;
};
